import { create } from 'zustand';
import { CollectionInterface, Permissions } from "ts-interfaces";
import { useLoginState, LoginState } from '../studioxr/auth/LoginState';
import { getSharedCollections } from '../v2/apis/collectionsSchemeApi';

interface CollectionsState {
    collections: CollectionInterface[];
    ownedCollections: CollectionInterface[];
    sharedCollections: CollectionInterface[];
    isLoading: boolean;
    permissionsRef: Permissions | null;
    initialFetchDone: boolean;
    sharedFetchDone: boolean;
    currentUserId: string | null;
    sharedCollectionIds: string[];
    hasMore: boolean;
    
    setCollections: (collections: CollectionInterface[]) => void;
    getNext: () => Promise<void>;
    getCollections: (userId: string, permissions: Permissions | null) => Promise<{
        collections: CollectionInterface[];
        setCollections: (collections: CollectionInterface[]) => void;
        getNext: () => Promise<void>;
        isLoading: boolean;
    }>;
    syncCollectionAssociations: (collectionsToAdd: string[], collectionsToRemove: string[], projectId: string) => void;
}

export const useCollections = create<CollectionsState>((set, get) => ({
    collections: [],
    ownedCollections: [],
    sharedCollections: [],
    isLoading: false,
    permissionsRef: null,
    initialFetchDone: false,
    sharedFetchDone: false,
    currentUserId: null,
    sharedCollectionIds: [],
    hasMore: true,
    setCollections: (collections) => {
        const currentUserId = get().currentUserId;
        const currentState = get();
        
        // Remove duplicates based on ID to prevent duplicate entries
        const uniqueCollections = collections.filter((collection, index, self) => 
            index === self.findIndex(c => c.id === collection.id)
        );
        
        // Check if this is a complete replacement or a partial update
        const isCompleteReplacement = uniqueCollections.length > currentState.ownedCollections.length || 
                                    uniqueCollections.some(col => col.uid !== currentUserId);
        
        let finalCollections;
        
        if (isCompleteReplacement) {
            finalCollections = uniqueCollections;
        } else {
            // This might be just owned collections, merge with existing shared collections
            // But first, remove any shared collections that might have been updated
            const updatedIds = new Set(uniqueCollections.map(c => c.id));
            const preservedSharedCollections = currentState.sharedCollections.filter(c => !updatedIds.has(c.id));
            
            finalCollections = [...uniqueCollections, ...preservedSharedCollections];
        }
        
        const ownedCollections = finalCollections.filter(col => col.uid === currentUserId);
        const sharedCollections = finalCollections.filter(col => col.uid !== currentUserId);
        
        set({ 
            collections: finalCollections, 
            ownedCollections,
            sharedCollections
        });
    },

    getNext: async () => {
        const loginState: LoginState = useLoginState.getState();

        // Check if profile.uid is available before proceeding
        if (!loginState.profile?.uid) {
            console.log("Waiting for profile.uid to be available");
            // Wait for profile.uid to be available
            await new Promise<void>((resolve) => {
                const checkProfile = () => {
                    const currentLoginState: LoginState = useLoginState.getState();
                    if (currentLoginState.profile?.uid) {
                        resolve();
                    } else {
                        console.log("Profile.uid not available yet, checking again...");
                        setTimeout(checkProfile, 500);  // check again in 500ms
                    }
                };
                checkProfile();
            });
        }

        set({ isLoading: true });
        
        try {
            await useLoginState.getState().getMoreCollections(20);
            
            const allCollections: CollectionInterface[] = useLoginState.getState().collections || [];
            const currentState = get();
            
            const updatedOwnedCollections: CollectionInterface[] = allCollections.filter(col => col.uid === currentState.currentUserId);
            const updatedSharedCollections = allCollections.filter(col => col.uid !== currentState.currentUserId);

            // Merge updated owned collections with existing shared collections
            const finalCollections = [...updatedOwnedCollections, ...updatedSharedCollections];
            
            // Check if we got any new collections
            const hasMore = updatedOwnedCollections.length > currentState.ownedCollections.length;

            set({ 
                collections: finalCollections,
                ownedCollections: updatedOwnedCollections,
                sharedCollections: updatedSharedCollections,
                isLoading: false,
                hasMore: hasMore
            });
            
        } catch (error) {
            console.error("Failed to fetch more collections:", error);
            set({ isLoading: false });
        }
    },

    getCollections: async (userId: string, permissions: Permissions | null) => {
        if (!userId) {
            return {
                collections: [],
                setCollections: get().setCollections,
                getNext: get().getNext,
                isLoading: false
            };
        }

        const state = get();

        const currentSharedIds = permissions 
            ? [...new Set([
                ...(permissions.collectionRead || []), 
                ...(permissions.collectionWrite || [])
              ])]
            : [];
        const sharedIdsChanged = JSON.stringify(currentSharedIds.sort()) !== JSON.stringify(state.sharedCollectionIds.sort());
        const needSharedFetch = !state.sharedFetchDone || sharedIdsChanged;

        const loginState = useLoginState.getState();
        const loadedCollections: CollectionInterface[] = loginState.collections || [];

        if (loadedCollections.length > 0 || (!state.initialFetchDone || userId !== state.currentUserId)) {
            set({ 
                currentUserId: userId,
                permissionsRef: permissions
            });

            // Start async fetch process
            (async () => {
                // Fetch shared collections if needed
                if (needSharedFetch && permissions) {
                    try {
                        const sharedCollections = await getSharedCollections(permissions);
                        
                        set({ 
                            sharedCollections: sharedCollections,
                            sharedFetchDone: true,
                            sharedCollectionIds: currentSharedIds
                        });
                    } catch (error) {
                        console.error("Error fetching shared collections:", error);
                    }
                }

                // Handle user collections
                if (loadedCollections.length > 0) {
                    set({ 
                        collections: loadedCollections, 
                        ownedCollections: loadedCollections.filter(col => col.uid === userId),
                        isLoading: false,
                        initialFetchDone: true
                    });
                } else {
                    set({ isLoading: true });
                    // Wait for collections to be loaded
                    await get().getNext();
                    set({ initialFetchDone: true });    
                }
            })().catch(error => console.error("Error initializing collections:", error));
        }
        
        // Always return the current state
        return {
            collections: get().collections,
            setCollections: get().setCollections,
            getNext: get().getNext,
            isLoading: get().isLoading
        };
    },

    syncCollectionAssociations: (collectionsToAdd: string[], collectionsToRemove: string[], projectId: string) => {
        set(state => {
            const updateCollection = (collection: CollectionInterface): CollectionInterface => {
                if (collectionsToAdd.includes(collection.id)) {
                    return { ...collection, associatedProjectId: projectId };
                }
                if (collectionsToRemove.includes(collection.id)) {
                    return { ...collection, associatedProjectId: undefined };
                }
                return collection;
            };

            // Apply the updateCollection function to all collection arrays
            const updatedCollections = state.collections.map(updateCollection);
            const updatedOwnedCollections = state.ownedCollections.map(updateCollection);
            const updatedSharedCollections = state.sharedCollections.map(updateCollection);

            // ALSO UPDATE useLoginState to keep both stores in sync
            const loginState = useLoginState.getState();
            const updatedLoginCollections = (loginState.collections || []).map(updateCollection);
            
            // Update the useLoginState store
            useLoginState.setState({
                collections: updatedLoginCollections as any // Cast to any to avoid type issues
            });

            return {
                collections: updatedCollections,
                ownedCollections: updatedOwnedCollections,
                sharedCollections: updatedSharedCollections
            };
        });
    },
}));